import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { intgApi, partnerOf, pollLabel } from '../../utils/integrationsApi';
import { formatStamp } from '../../utils/datetime';
import '../../styles/Integrations.css';

/**
 * Integrations home (mockup screen 1): one card per integration — the two sides with the
 * direction between them, how often it checks, the last run, and its state.
 *
 * Design: design/DESIGN_INTEGRATIONS.md. Only an Orcanos admin may create or change one (D2);
 * everyone licensed sees the cards and the run log.
 */
export function StatusPill({ integ }) {
  if (!integ.enabled) return <span className="intg-pill intg-pill--off">Off</span>;
  if (integ.running_since) return <span className="intg-pill intg-pill--on">Checking…</span>;
  const s = integ.last_status;
  if (s === 'error') return <span className="intg-pill intg-pill--bad" title={integ.last_error}>Failing</span>;
  if (s === 'partial') return <span className="intg-pill intg-pill--bad">Some items failed</span>;
  if (s === 'warning') return <span className="intg-pill intg-pill--warn">Warnings</span>;
  return <span className="intg-pill intg-pill--on">On</span>;
}

export function flowArrow(fields) {
  const dirs = new Set((fields || []).filter((f) => (f.kind || 'field') === 'field').map((f) => f.direction));
  if (dirs.has('both') || (dirs.has('to_jira') && dirs.has('to_orcanos'))) return '⇄';
  if (dirs.has('to_orcanos')) return '←';
  return '→';
}

export function Sides({ integ }) {
  const orc = [integ.orc_project_name, integ.orc_version_label && `v${integ.orc_version_label}`,
    integ.orc_type_label, integ.orc_filter_name && `“${integ.orc_filter_name}”`].filter(Boolean).join(' · ');
  const gh = integ.connector === 'github';
  const jira = [integ.jira_project_key, !(gh && integ.jira_issue_type_id === 'issue') && integ.jira_issue_type_name]
    .filter(Boolean).join(' · ');
  return (
    <div className="intg-flow">
      <span className="intg-side intg-side--orc">Orcanos{orc ? ` · ${orc}` : ''}</span>
      <span className="intg-arrow" aria-label="direction">{flowArrow(integ.fields)}</span>
      <span className={`intg-side ${gh ? 'intg-side--gh' : 'intg-side--jira'}`}>{partnerOf(integ)}{jira ? ` · ${jira}` : ''}</span>
    </div>
  );
}

const APP_TEXT = {
  jira: {
    title: 'Atlassian app (for Connect with Atlassian)',
    intro: 'Connect with Atlassian signs in through your company\'s Atlassian login and needs your own Atlassian app. It is stored encrypted and used only by this account. Without it, use an API token in each integration.',
    how: 'Create an OAuth 2.0 integration at developer.atlassian.com with the Jira scopes read:jira-work, write:jira-work, read:jira-user and manage:jira-webhook, turn on Distribution → Sharing, and add this callback URL:',
    ok: 'Saved. Atlassian recognised your app.',
    remove: 'Remove this account\'s Atlassian app? Integrations connected with it stop syncing until they are reconnected.',
    change: 'Changing or removing the app means each integration connected with Atlassian has to connect again.',
  },
  github: {
    title: 'GitHub app (for Connect with GitHub)',
    intro: 'Connect with GitHub signs in through GitHub (and your organisation\'s SSO) and needs your own GitHub OAuth App. It is stored encrypted and used only by this account. Without it, use a personal access token in each integration.',
    how: 'At GitHub → Settings → Developer settings → OAuth Apps → New OAuth App, set the Authorization callback URL to the address below. Use an OAuth App, not a GitHub App.',
    ok: 'Saved. GitHub recognised your app.',
    remove: 'Remove this account\'s GitHub app? Integrations connected with it keep their sign-in, but nobody can connect a new one with it.',
    change: 'An integration already connected keeps working; changing the app affects new connections only.',
  },
};

/**
 * THIS account's own Atlassian app (v4.26.0) or GitHub OAuth App (v4.28.0) — the only one
 * "Connect with …" can use. There is no shared Orcanos app: many customers share one database,
 * so each account's credentials are entered here by its own admin and stored encrypted under its
 * account only. The secret is write-only. Without an app, a token still works.
 */
function OAuthAppBox({ provider }) {
  const T = APP_TEXT[provider];
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_id: '', client_secret: '' });
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const x = await intgApi.oauthApp(provider);
      setD(x);
      setForm({ client_id: x.client_id || '', client_secret: '' });
    } catch (e) { setMsg({ ok: false, text: e.message }); }
  }, [provider]);
  useEffect(() => { if (open && !d) load(); }, [open, d, load]);

  const send = async (body, okText) => {
    setBusy(true);
    setMsg(null);
    try {
      const x = await intgApi.saveOauthApp(body, provider);
      setD(x);
      setForm({ client_id: x.client_id || '', client_secret: '' });
      setMsg({ ok: true, text: okText });
    } catch (e) { setMsg({ ok: false, text: e.message }); }
    setBusy(false);
  };

  return (
    <details className="intg-appbox" open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary>{T.title}{d ? (d.active ? ' · set up' : ' · not set up') : ''}</summary>
      {!d ? <p className="intg-sub">Loading…</p> : (
        <div className="intg-stack">
          <p className="intg-hint">{T.intro}</p>
          <p className="intg-hint">{T.how}</p>
          {(d.callback_urls || []).map((u) => <code key={u} className="intg-mono intg-break">{u}</code>)}
          <div className="intg-grid2">
            <label className="intg-field"><span>Client ID</span>
              <input id={`app-cid-${provider}`} value={form.client_id} onChange={(e) => setForm((f) => ({ ...f, client_id: e.target.value }))} />
            </label>
            <label className="intg-field"><span>{provider === 'github' ? 'Client secret' : 'Secret'}</span>
              <input id={`app-sec-${provider}`} type="password" autoComplete="new-password" value={form.client_secret}
                     placeholder={d.has_secret ? 'Saved. Type a new one to replace it' : ''}
                     onChange={(e) => setForm((f) => ({ ...f, client_secret: e.target.value }))} />
            </label>
          </div>
          <p className="intg-hint">{T.change}</p>
          <div className="intg-row-actions">
            <button type="button" className="intg-btn intg-btn--primary" disabled={busy}
                    onClick={() => send(form, T.ok)}>{busy ? 'Checking…' : 'Save'}</button>
            {d.active && (
              <button type="button" className="intg-btn intg-btn--ghost" disabled={busy}
                      onClick={() => window.confirm(T.remove)
                        && send({ clear: true }, 'Removed.')}>Remove</button>
            )}
            {msg && <span className={msg.ok ? 'intg-ok' : 'intg-bad'}>{msg.text}</span>}
          </div>
        </div>
      )}
    </details>
  );
}

export default function IntegrationsHome() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const navigate = useNavigate();
  // The left nav opens this page per connector (`?type=jira|github`, v4.28.0); no type = all.
  const [params] = useSearchParams();
  const type = ['jira', 'github'].includes(params.get('type')) ? params.get('type') : '';

  const load = useCallback(async () => {
    try {
      setData(await intgApi.list());
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (connector) => {
    setBusy(true);
    try {
      const d = await intgApi.create({ name: 'New integration', connector });
      navigate(`/integrations/${d.id}/edit`);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  const runNow = async (id) => {
    try {
      await intgApi.runNow(id);
      setTimeout(load, 1500);
    } catch (e) { setError(e.message); }
  };

  // A switched-off copy of the setup (no links, no history); opens it to change what differs.
  const duplicate = async (id) => {
    setBusy(true);
    try {
      const d = await intgApi.duplicate(id);
      navigate(`/integrations/${d.id}/edit`);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await intgApi.remove(id);
      setConfirmDelete(null);
      load();
    } catch (e) { setError(e.message); }
  };

  const canEdit = data?.can_edit;
  const rows = (data?.integrations || []).filter((it) => !type || (it.connector || 'jira') === type);
  // Each connector has its own licence (account-management: Jira / GitHub switches, v4.28.0).
  const can = (c) => canEdit && (data?.connectors || []).includes(c) && (!type || type === c);
  const P = type === 'github' ? 'GitHub' : type === 'jira' ? 'Jira' : '';

  return (
    <div className="intg-page">
      <div className="intg-head">
        <div>
          <h1>{P ? `${P} integrations` : 'Integrations'}</h1>
          <p className="intg-sub">Keep Orcanos items and {P || 'Jira or GitHub'} issues in step, both ways.</p>
        </div>
        <span className="intg-grow" />
        {canEdit && (
          <div className="intg-row-actions">
            {can('jira') && (
              <button type="button" className="intg-btn intg-btn--primary" onClick={() => create('jira')} disabled={busy}>
                + Jira integration
              </button>
            )}
            {can('github') && (
              <button type="button" className="intg-btn intg-btn--primary" onClick={() => create('github')} disabled={busy}>
                + GitHub integration
              </button>
            )}
          </div>
        )}
      </div>

      {error && <div className="intg-note intg-note--bad" role="alert">{error}</div>}
      {data === null && !error && <p className="intg-sub">Loading…</p>}

      {data && rows.length === 0 && (
        <div className="intg-empty">
          <h2>No integrations yet</h2>
          <p>
            An integration links one Orcanos filter to one Jira project or GitHub repository. New and
            changed items are copied across, field by field, and each side gets the other's ID and link.
          </p>
          {canEdit
            ? (
              <div className="intg-row-actions">
                {can('jira') && <button type="button" className="intg-btn intg-btn--primary" onClick={() => create('jira')} disabled={busy}>Connect to Jira</button>}
                {can('github') && <button type="button" className="intg-btn intg-btn--primary" onClick={() => create('github')} disabled={busy}>Connect to GitHub</button>}
              </div>
            )
            : <p className="intg-sub">Ask an Orcanos administrator to set one up.</p>}
        </div>
      )}

      <div className="intg-list">
        {rows.map((it) => (
          <div className="intg-card" key={it.id}>
            <div className="intg-card-main">
              <h2>{it.name || 'Untitled integration'}</h2>
              <Sides integ={it} />
              <div className="intg-meta">
                <span>{pollLabel(it.poll_minutes)}</span>
                <span>Last check {it.last_run_at ? formatStamp(it.last_run_at) : 'never'}</span>
                <span>{it.n_links} linked item{it.n_links === 1 ? '' : 's'}</span>
                <span>{{ orcanos: 'Orcanos wins', jira: `${partnerOf(it)} wins`, latest: 'Latest change wins' }[it.conflict_rule]} on conflict</span>
              </div>
              {it.enabled && it.last_status === 'error' && it.last_error && (
                <div className="intg-card-error">{it.last_error}</div>
              )}
            </div>
            <div className="intg-card-actions">
              <StatusPill integ={it} />
              {canEdit && <Link className="intg-btn intg-btn--small" to={`/integrations/${it.id}/edit`}>{it.enabled ? 'Edit' : 'Continue setup'}</Link>}
              <Link className="intg-btn intg-btn--small" to={`/integrations/${it.id}/log`}>Run log</Link>
              {canEdit && it.enabled && (
                <button type="button" className="intg-btn intg-btn--small" onClick={() => runNow(it.id)}>Run now</button>
              )}
              {can(it.connector || 'jira') && (
                <button type="button" className="intg-btn intg-btn--small" onClick={() => duplicate(it.id)} disabled={busy}
                        title="Copy this setup into a new integration, switched off">Duplicate</button>
              )}
              {canEdit && (confirmDelete === it.id ? (
                <span className="intg-confirm">
                  Delete? Linked items stay as they are.
                  <button type="button" className="intg-btn intg-btn--small intg-btn--danger" onClick={() => remove(it.id)}>Delete</button>
                  <button type="button" className="intg-btn intg-btn--small" onClick={() => setConfirmDelete(null)}>Keep</button>
                </span>
              ) : (
                <button type="button" className="intg-btn intg-btn--small intg-btn--ghost" onClick={() => setConfirmDelete(it.id)} aria-label={`Delete ${it.name}`}>Delete</button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {can('jira') && <OAuthAppBox provider="jira" />}
      {can('github') && <OAuthAppBox provider="github" />}
    </div>
  );
}
